import { create } from 'zustand';
import { getDomain } from '@/lib/utils';

interface StoreInfo {
  site_name: string;
  slogan: string;
  site_logo: string;
  about_us: string;
  contact_email: string;
  phone_num: string;
  shop_address: string;
  shop_ig: string;
  shop_wa: string;
  shop_tg: string;
  google_map: string;
  whatsFloat: number;
  currenCy?: string;
  currency?: string;
  currency_symbol?: string;
  checkout_note: string;
  // Bank transfer fields
  m_bank: string;
  m_ac_name: string;
  m_ac_num: string;
  mb_active: number;
  // Paystack fields
  pays_pk: string;
  pays_active: number;
  // Monnify fields
  monify_api: string;
  monify_active: number;
  // Stripe fields
  stripeActive: number;
  StripeKey: string;
  StripeSecrete: string;
  // Split payment fields
  sepShipping: number;
  sepShippingAcn: string;
  heroSlider: string[];
  site_lock?: number;
  id?: number;
}

interface StoreState {
  storeInfo: StoreInfo | null;
  setStoreInfo: (info: StoreInfo) => void;
  fetchStoreInfoByDomain: (domain: string) => Promise<void>;
  error: string | null;
  setError: (error: string | null) => void;
}

export const useStoreInfo = create<StoreState>((set) => ({
  storeInfo: (window as any).__INITIAL_STATE__ || null,
  error: null,
  setError: (error) => set({ error }),
  setStoreInfo: (info) => {
    set({ storeInfo: info });
    localStorage.setItem('currency', info.currenCy || 'NGN');
  },
  fetchStoreInfoByDomain: async (domain: string) => {
    try {
      if ((window as any).__INITIAL_STATE__) {
        set({ storeInfo: (window as any).__INITIAL_STATE__ });
        return;
      }

      const cachedInfo = localStorage.getItem('storeInfo');
      if (cachedInfo) {
        const parsedInfo = JSON.parse(cachedInfo);
        set({ storeInfo: parsedInfo });
      }

      const apiEndpoint = import.meta.env.VITE_API_ENDPOINT;
      if (!apiEndpoint) {
        throw new Error('API endpoint not configured');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${apiEndpoint}/api/v1/store/store-info?domain=${domain}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to connect to server. Please check your connection and try again.');
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Invalid response from server. Please try again.');
      }

      const data = await response.json();
      if (!data.status) {
        throw new Error(data.message || 'Store information not found. Please try again.');
      }

      const storeInfo = {
        ...data.store,
        heroSlider: data.heroSlider || [],
      };

      localStorage.setItem('storeInfo', JSON.stringify(storeInfo));
      set({ storeInfo, error: null });

      return storeInfo;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load store information. Please refresh the page.';
      set({ error: errorMessage });
      console.error('Store info fetch error:', error);
      
      const cachedInfo = localStorage.getItem('storeInfo');
      if (cachedInfo) {
        const parsedInfo = JSON.parse(cachedInfo);
        set({ storeInfo: parsedInfo });
      }
    }
  }
}));

export async function fetchStoreInfo() {
  const { fetchStoreInfoByDomain } = useStoreInfo.getState();
  const domain = getDomain();
  return fetchStoreInfoByDomain(domain);
}

